import PropTypes from 'prop-types';
import cx from 'classnames';
import OrderTitle from '@app/components/common/OrderDetails/OrderTitle';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SummaryOrderTotals from '@app/components/pages/summary-and-payment/SummaryOrderTotals';
import ExceptionFeedbackBySelector from '@app/components/common/Exception/ExceptionFeedbackBySelector';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import Conditional from '@app/components/common/Conditional';
import {
  BILLING_METHOD_CAT_PREPAID_CERT,
  CAT_DEALER_SHIPPING_TAX_PREFERENCES,
  STATUS
} from '@app/constants/commonConstants';

const SummaryOfCharges = ({ className, hasTitle, orderId }) => {
  const { t } = useTranslation();
  const currency = useSelector(s => s?.orders?.byId[orderId]?.currency);
  const orderTotals = useSelector(
    s => s?.orders?.byId[orderId]?.orderTotals?.formatted
  );
  const taxLabels = useSelector(s => s?.orders?.byId[orderId]?.taxLabels);

  const orderPriceAvailabilityTotalsStatus = useSelector(
    s => s?.orders?.byId[orderId]?.orderPriceAvailabilityTotalsStatus
  );

  const paymentMethodInfo =
    useSelector(
      s => s?.orders?.byId[orderId]?.billingAndPayment?.paymentMethodInfo
    ) || [];
  const hasCreditCatBillingMethod = paymentMethodInfo.some?.(
    ({ code }) => code === BILLING_METHOD_CAT_PREPAID_CERT
  );

  // We're looking for other method different than cat credits
  const hasOtherBillingMethodAvailable = paymentMethodInfo.some?.(
    ({ code }) => code !== BILLING_METHOD_CAT_PREPAID_CERT
  );
  const billingMethod =
    paymentMethodInfo.find?.(
      ({ code }) => code !== BILLING_METHOD_CAT_PREPAID_CERT
    ) || {};

  const showBillingMethods =
    hasCreditCatBillingMethod || hasOtherBillingMethodAvailable;

  const catCreditTotal = useSelector(
    s => s?.orders?.byId[orderId]?.orderTotals?.formatted?.orderCatCreditsAmount
  );

  const catCreditTotalUnformatted = useSelector(
    s =>
      s?.orders?.byId[orderId]?.orderTotals?.unformatted?.orderCatCreditsAmount
  );

  const orderRemainingPaymentAmount = useSelector(
    s =>
      s?.orders?.byId[orderId]?.orderTotals?.formatted
        ?.orderAdditionalPaymentAmount
  );

  const isEnableZeroFreightMsg = useSelector(
    s => s?.dealer?.enableZeroFreightMsg
  );

  const shippingCost = useSelector(
    s => s?.orders?.byId[orderId]?.orderTotals?.unformatted?.shipping
  );

  const dealerShippingChargePreference = useSelector(
    s => s.common?.dealerShippingChargePreference
  );

  const isDealerShippingChargePreferenceEnabled =
    dealerShippingChargePreference === CAT_DEALER_SHIPPING_TAX_PREFERENCES;

  const hasShippingCost = shippingCost === 0;

  const showZeroFreightMsg =
    isDealerShippingChargePreferenceEnabled &&
    isEnableZeroFreightMsg &&
    hasShippingCost;

  const isRejected = orderPriceAvailabilityTotalsStatus === STATUS.REJECTED;
  const errorFeedbackSelector = state =>
    state?.errors?.[ERROR_DOMAIN.ORDER_CONFIRMATION]?.[
      ERROR_PATH.ORDER_CONFIRMATION_TOTAL
    ];

  const catCreditsSalesforceAmount =
    orderTotals?.orderCatCreditsAmountSalesforceCurrency;

  const salesforceCurrency = useSelector(
    s => s?.orders?.byId[orderId]?.salesforceCurrency
  );

  const catCardSalesforceAmount =
    orderTotals?.orderAdditionalPaymentAmountSalesforceCurrency;

  return (
    <div className={cx('w-100', className)}>
      <ExceptionFeedbackBySelector
        className="mx-1"
        selector={errorFeedbackSelector}
        showReferenceId
      />
      <Conditional test={!isRejected}>
        <>
          {hasTitle && <OrderTitle>{t('SUMMARY_OF_CHARGES')}</OrderTitle>}
          <SummaryOrderTotals
            currency={currency}
            subtotal={orderTotals?.orderSubTotal}
            shippingCost={orderTotals?.shipping}
            tax={orderTotals?.totalTax}
            total={orderTotals?.orderTotal}
            orderAdjustment={orderTotals?.orderAdjustment}
            vatTaxEnabled={orderTotals?.vatTaxEnabled}
            customLabelClassName={[
              'u-color--ExtraDarkGray text-univers--bold text-sm mb-0 '
            ]}
            customValueClassName={['u-color--DarkGray text-sm mb-0']}
            chargedCustomClass="text-xs u-color--DarkGray"
            taxLabel={taxLabels?.map(({ label, formattedTax }) => ({
              label,
              value: formattedTax
            }))}
            hasCreditCatBillingMethod={hasCreditCatBillingMethod}
            isOrderConfirmation
            catCreditTotal={catCreditTotal}
            catCreditTotalUnformatted={catCreditTotalUnformatted}
            orderRemainingPaymentAmount={orderRemainingPaymentAmount}
            billingMethod={billingMethod?.label}
            showBillingMethods={showBillingMethods}
            zeroFreightMessageEnabled={showZeroFreightMsg}
            catCreditsSalesforceAmount={catCreditsSalesforceAmount}
            catCardSalesforceAmount={catCardSalesforceAmount}
            salesforceCurrency={salesforceCurrency}
          />
        </>
      </Conditional>
    </div>
  );
};

SummaryOfCharges.propTypes = {
  className: PropTypes.string,
  hasTitle: PropTypes.bool,
  orderId: PropTypes.string
};

export default SummaryOfCharges;
